import { ApiStatus, AppServices } from 'network'
import { useKlineStore } from './use-kline'
import { useBaseInfoStore } from './use-base-info'
import { useAssetsStore } from './use-assets'

import { PLATFORM_CONFIG } from 'platform-config'
import { useUserInfoStore } from './use-user-info'
import { useQuoteStore } from './use-quote'
import { useChangeLanguage } from '../hooks/use-change-language'
import dayjs from 'dayjs'
import { SvIds, useStaticTableDataStore } from '..'
import { useI18n } from 'i18n-utils'

export enum SpotOrderTabType {
  Position = 'position',
  Current = 'current',
  History = 'history',
}

type TabItem = {
  label: string
  value: SpotOrderTabType
}

export enum OrderStatus {
  /** 未成交 */
  Pending = 0,
  /** 部分成交 */
  Partial = 1,
  /** 完全成交 */
  Success = 2,
  /** 已撤单 */
  Canceled = 3,
}

export const defaultStartTime = dayjs('2020-01-01').toDate()
export const defaultEndTime = dayjs().toDate()

export const useSpotOrderListStore = defineStore('spotOrderListStore', () => {
  const currentOrderList = ref<ApiResponse.SpotOrderItem[]>([])
  const historyOrderList = ref<ApiResponse.SpotOrderItem[]>([])
  const klineStore = useKlineStore()
  const baseInfoStore = useBaseInfoStore()
  const loading = ref(false)
  const timeout = ref<NodeJS.Timeout | null>(null)
  const assetStore = useAssetsStore()
  const hideCanceledOrder = ref(false)
  const hideOtherOrder = ref(false)
  const statusList = ref<OrderStatus[]>([OrderStatus.Pending, OrderStatus.Partial, OrderStatus.Success, OrderStatus.Canceled])
  const quoteStore = useQuoteStore()
  const getHistoryOrderTimeRange = ref<[Date, Date]>([defaultStartTime, defaultEndTime])
  const staticStore = useStaticTableDataStore()
  const { t } = useI18n()

  const pageInfo = ref<PageInfo>({
    pageCount: 0,
    pageCurrent: 1,
    pageSize: 10,
    pageTotal: 0,
  })

  const mobileHistoryOrderList = ref<ApiResponse.SpotOrderItem[]>([])
  const historyOrderListFilter = ref<{
    startTime?: number
    endTime?: number
    symbolId?: SymbolId | string
    side?: 'buy' | 'sell' | ''
  }>({
    side: '',
  })

  const loadingHistoryOrderList = ref(false)
  const userInfoStore = useUserInfoStore()
  const loadingCurrentOrderList = ref(false)

  const getCurrentOrderList = async () => {
    if (!userInfoStore.isLogin) return
    loadingCurrentOrderList.value = true
    stopGetCurrentOrderList()
    const res = await AppServices.getSpotOrderList()
    currentOrderList.value = res.data
    loadingCurrentOrderList.value = false
    timeout.value = setTimeout(() => {
      getCurrentOrderList()
    }, 2000)
  }

  const getHistoryOrderList = async (data?: { page?: number; pageSize?: number }) => {
    if (!userInfoStore.isLogin) return
    if (loadingHistoryOrderList.value) return
    loadingHistoryOrderList.value = true
    const page = data?.page || pageInfo.value.pageCurrent
    const pageSize = data?.pageSize || pageInfo.value.pageSize
    let status = statusList.value.join(',')
    if (hideCanceledOrder.value) {
      status = statusList.value.filter((item) => item !== OrderStatus.Canceled).join(',')
    }
    let symbolId: SymbolId | string | undefined = undefined
    if (hideOtherOrder.value) {
      symbolId = klineStore.currentSymbolId
    } else {
      symbolId = historyOrderListFilter.value.symbolId
    }
    const res = await AppServices.getHistoryOrderList({
      ...historyOrderListFilter.value,
      page,
      pageSize,
      status,
      symbolId: symbolId,
      side: historyOrderListFilter.value.side || '',
    })
    loadingHistoryOrderList.value = false
    if (res.status !== ApiStatus.Success) {
      return
    }
    historyOrderList.value = res.data.list
    if (page === 1) {
      mobileHistoryOrderList.value = res.data.list
    } else {
      mobileHistoryOrderList.value.push(...res.data.list)
    }
    pageInfo.value = res.data.pageInfo
  }

  const getCoinFromSymbolName = (coinId: CoinId) => {
    const staticData = baseInfoStore.getCoinInfoByItemId(coinId)
    if (!staticData) return ''
    return staticData.itemName?.toUpperCase()
  }

  const tabs = ref<TabItem[]>([
    { label: '持仓', value: SpotOrderTabType.Position },
    { label: '当前委托', value: SpotOrderTabType.Current },
  ])

  const statusMap = ref<Record<OrderStatus, string>>({
    [OrderStatus.Pending]: '未成交',
    [OrderStatus.Partial]: '部分成交',
    [OrderStatus.Success]: '完全成交',
    [OrderStatus.Canceled]: '已撤单',
  })

  const activeTab = ref<SpotOrderTabType>(SpotOrderTabType.Position)

  const onChangeTab = (tab: SpotOrderTabType) => {
    activeTab.value = tab
    if (tab === SpotOrderTabType.History) {
      stopGetCurrentOrderList()
    } else {
      getCurrentOrderList()
    }
  }

  const onCancelOrder = async (trustId: ApiResponse.SpotOrderItem['trustId']) => {
    loading.value = true
    const res = await AppServices.cancelOrder({ trustId })
    if (res.status === 200) {
      refresh()
    }
    loading.value = false
    return res.status === 200
  }

  const refresh = async () => {
    if (timeout.value) {
      clearTimeout(timeout.value)
    }
    await getCurrentOrderList()
    assetStore.refresh()
  }

  const getStatusColor = (status: ApiResponse.SpotOrderItem['status']) => {
    if (status === OrderStatus.Success) {
      return PLATFORM_CONFIG.colors.up
    }
    if (status === OrderStatus.Canceled) {
      return PLATFORM_CONFIG.colors.down
    }
    return PLATFORM_CONFIG.colors.text.secondary
  }

  watch(hideCanceledOrder, () => {
    getHistoryOrderList()
  })

  watch(hideOtherOrder, () => {
    getHistoryOrderList()
  })

  /** 停止刷新当前委托 */
  const stopGetCurrentOrderList = () => {
    if (timeout.value) {
      clearTimeout(timeout.value)
      timeout.value = null
    }
  }

  const onLoadMoreHistoryOrderList = () => {
    if (loadingHistoryOrderList.value) return
    if (pageInfo.value.pageCurrent >= pageInfo.value.pageCount) return
    getHistoryOrderList({ page: pageInfo.value.pageCurrent + 1 })
  }

  const spotStaticList = computed(() => {
    const spotList = staticStore.staticTableDataMap[SvIds.StaticSymbol]
    return Object.values(spotList).map((item) => {
      return {
        value: item.symbolId,
        label: quoteStore.getSpotSymbolName(item.symbolId),
      }
    })
  })

  useChangeLanguage(async () => {
    quoteStore.listenMarketM()
  })

  const onResetHistoryOrderListFilter = () => {
    historyOrderListFilter.value = {}
    pageInfo.value.pageCurrent = 1
    getHistoryOrderTimeRange.value = [defaultStartTime, defaultEndTime]
    historyOrderListFilter.value.startTime = defaultStartTime.getTime()
    historyOrderListFilter.value.endTime = defaultEndTime.getTime()
    getHistoryOrderList()
  }

  const onQueryHistoryOrderList = () => {
    pageInfo.value.pageCurrent = 1
    historyOrderListFilter.value.startTime = getHistoryOrderTimeRange.value[0].getTime()
    historyOrderListFilter.value.endTime = getHistoryOrderTimeRange.value[1].getTime()
    getHistoryOrderList()
  }

  const sideOptions = computed(() => {
    return [
      {
        label: t('买入'),
        value: 'buy',
      },
      {
        label: t('卖出'),
        value: 'sell',
      },
    ]
  })

  return {
    currentOrderList,
    historyOrderList,
    getCoinFromSymbolName,
    tabs,
    activeTab,
    onChangeTab,
    onCancelOrder,
    loading,
    refresh,
    getHistoryOrderList,
    pageInfo,
    loadingHistoryOrderList,
    statusMap,
    getStatusColor,
    hideCanceledOrder,
    hideOtherOrder,
    stopGetCurrentOrderList,
    getCurrentOrderList,
    onLoadMoreHistoryOrderList,
    mobileHistoryOrderList,
    spotStaticList,
    getHistoryOrderTimeRange,
    onResetHistoryOrderListFilter,
    loadingCurrentOrderList,
    historyOrderListFilter,
    onQueryHistoryOrderList,
    sideOptions,
  }
})
